// Service for managing Clerk users and roles
// This will need backend API integration with Clerk admin endpoints

// import { clerkApiClient } from './clerk-api-client'; // Will be used when backend is implemented

export interface ClerkUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
  role: 'admin' | 'super_admin' | 'instructor' | null;
  createdAt: string;
  lastSignInAt: string | null;
  publicMetadata: Record<string, unknown>;
}

export type RoleType = 'admin' | 'super_admin' | 'instructor';

class ClerkUserService {
  /**
   * Fetch all Clerk users (requires backend API with Clerk admin access)
   * TODO: Implement backend endpoint at /clerk/users
   */
  static async getAllUsers(): Promise<ClerkUser[]> {
    try {
      // TODO: Replace with actual API call when backend is ready
      // return clerkApiClient.get<ClerkUser[]>('/clerk/users');
      
      // Mock data for development - remove when backend is implemented
      return this.getMockUsers();
    } catch (error) {
      throw new Error('Failed to fetch users from Clerk');
    }
  }

  /**
   * Get users filtered by role
   */
  static async getUsersByRole(role: RoleType): Promise<ClerkUser[]> {
    const allUsers = await this.getAllUsers();
    return allUsers.filter(user => user.role === role);
  }

  /**
   * Get admin users (both admin and super_admin)
   */
  static async getAdminUsers(): Promise<ClerkUser[]> {
    const allUsers = await this.getAllUsers();
    return allUsers.filter(user => user.role === 'admin' || user.role === 'super_admin');
  }

  /**
   * Get instructor users
   */
  static async getInstructorUsers(): Promise<ClerkUser[]> {
    return this.getUsersByRole('instructor');
  }

  /**
   * Update user role in Clerk metadata
   * TODO: Implement backend endpoint at /clerk/users/{userId}/role
   */
  static async updateUserRole(userId: string, role: RoleType | null): Promise<ClerkUser> {
    try {
      // TODO: Replace with actual API call when backend is ready
      // return clerkApiClient.put<ClerkUser>(`/clerk/users/${userId}/role`, { role });
      
      // Mock implementation for development
      const users = await this.getAllUsers();
      const user = users.find(u => u.id === userId);
      if (!user) {
        throw new Error('User not found');
      }
      
      // Simulate role update
      const updatedUser = { ...user, role };
      // Mock implementation - would normally make API call here
      return updatedUser;
    } catch (error) {
      throw new Error('Failed to update user role');
    }
  }

  /**
   * Search users by email or name
   */
  static async searchUsers(query: string): Promise<ClerkUser[]> {
    const allUsers = await this.getAllUsers();
    const lowercaseQuery = query.toLowerCase();
    
    return allUsers.filter(user => 
      user.email.toLowerCase().includes(lowercaseQuery) ||
      (user.firstName && user.firstName.toLowerCase().includes(lowercaseQuery)) ||
      (user.lastName && user.lastName.toLowerCase().includes(lowercaseQuery))
    );
  }

  /**
   * Get users available for promotion to a specific role
   */
  static async getPromotableUsers(targetRole: RoleType): Promise<ClerkUser[]> {
    const allUsers = await this.getAllUsers();
    
    if (targetRole === 'instructor') {
      // For instructor role, exclude those already instructors
      return allUsers.filter(user => user.role !== 'instructor');
    } else {
      // For admin roles, exclude those already with admin roles
      return allUsers.filter(user => user.role !== 'admin' && user.role !== 'super_admin');
    }
  }

  /**
   * Mock data for development purposes
   * TODO: Remove when actual Clerk integration is implemented
   */
  private static getMockUsers(): ClerkUser[] {
    return [
      {
        id: 'user_2nKz8Hv3mP9QqRsT',
        email: 'admin@thesolroom.com',
        firstName: 'Admin',
        lastName: 'User',
        imageUrl: null,
        role: 'admin',
        createdAt: '2024-01-01T00:00:00Z',
        lastSignInAt: '2024-01-15T10:30:00Z',
        publicMetadata: { role: 'admin' },
      },
      {
        id: 'user_8JkL3Mn4pQ7RsT9u',
        email: 'kunalg@thesolroom.com',
        firstName: 'Kunal',
        lastName: 'Goel',
        imageUrl: null,
        role: 'super_admin',
        createdAt: '2024-01-01T00:00:00Z',
        lastSignInAt: '2024-01-16T09:15:00Z',
        publicMetadata: { role: 'super_admin' },
      },
      {
        id: 'user_5Vw6Xy7zA8BcD2eF',
        email: 'jane.instructor@thesolroom.com',
        firstName: 'Jane',
        lastName: 'Smith',
        imageUrl: null,
        role: 'instructor',
        createdAt: '2024-01-05T00:00:00Z',
        lastSignInAt: '2024-01-14T14:20:00Z',
        publicMetadata: { role: 'instructor' },
      },
      {
        id: 'user_1GhI4JkL7MnP0qRs',
        email: 'instructor2@thesolroom.com',
        firstName: 'Mike',
        lastName: 'Johnson',
        imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
        role: 'instructor',
        createdAt: '2024-01-08T00:00:00Z',
        lastSignInAt: '2024-01-13T11:45:00Z',
        publicMetadata: { role: 'instructor' },
      },
      {
        id: 'user_9TuV2Wx3Yz4A5bCd',
        email: 'regular.user@example.com',
        firstName: 'Sarah',
        lastName: 'Wilson',
        imageUrl: null,
        role: null,
        createdAt: '2024-01-10T00:00:00Z',
        lastSignInAt: '2024-01-12T16:45:00Z',
        publicMetadata: {},
      },
      {
        id: 'user_6EfG7HiJ8KlM9nOp',
        email: 'new.member@example.com',
        firstName: 'David',
        lastName: 'Brown',
        imageUrl: null,
        role: null,
        createdAt: '2024-01-12T00:00:00Z',
        lastSignInAt: '2024-01-15T08:30:00Z',
        publicMetadata: {},
      },
      {
        id: 'user_3QrS4TuV5WxY6zA7',
        email: 'another.user@example.com',
        firstName: 'Emily',
        lastName: 'Davis',
        imageUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b1a0?w=150',
        role: null,
        createdAt: '2024-01-11T00:00:00Z',
        lastSignInAt: null,
        publicMetadata: {},
      },
    ];
  }
}

export default ClerkUserService;